import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Search, Printer, Download, Eye, Edit, Trash2 } from 'lucide-react';
import { Api } from '../../services/Api/Api';
import ViewAnnonceModal from '../../components/Annonce/ViewAnnonceModal';
import EditAnnonceModal from '../../components/Annonce/EditAnnonceModal';
import './Annonces.css';

const Annonces = () => {
    const [annoncesData, setAnnoncesData] = useState({
        count: 0,
        next: null,
        previous: null,
        results: []
    });
    const [filteredAnnonces, setFilteredAnnonces] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('toutes');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showViewModal, setShowViewModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedAnnonce, setSelectedAnnonce] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const navigate = useNavigate();
    const location = useLocation();

    // Endpoint API
    const ANNONCES_ENDPOINT = 'formalite/annonces/get-annonces';

    // Mapping des statuts API vers les onglets
    const STATUS_MAPPING = {
        'en-attente': ['brouillon'],
        'validees': ['validee', 'validée'],
        'publiees': ['publiee', 'publiée'],
        'rejetees': ['rejetee', 'rejetée'],
        'toutes': []
    };

    // Déterminer l'onglet actif basé sur l'URL
    const getActiveTab = () => {
        const path = location.pathname;
        if (path.includes('en-attente')) return 'en-attente';
        if (path.includes('validees')) return 'validees';
        if (path.includes('publiees')) return 'publiees';
        if (path.includes('rejetees')) return 'rejetees';
        if (path.includes('toutes')) return 'toutes';
        return 'en-attente'; // défaut
    };

    const activeTab = getActiveTab();

    // Récupérer la liste des annonces
    const fetchAnnonces = async (page = 1, search = '', status = '') => {
        try {
            setLoading(true);
            setError(null);

            let endpoint = ANNONCES_ENDPOINT;
            const params = new URLSearchParams();

            if (page > 1) {
                params.append('page', page);
            }

            if (search) {
                params.append('search', search);
            }

            // Mapper le statut de l'onglet vers le statut API
            if (status && status !== 'toutes') {
                const apiStatuses = STATUS_MAPPING[status];
                if (apiStatuses && apiStatuses.length > 0) {
                    params.append('status', apiStatuses[0]);
                }
            }

            if (params.toString()) {
                endpoint += `?${params.toString()}`;
            }

            const response = await Api.get(endpoint);
            setAnnoncesData(response || { count: 0, next: null, previous: null, results: [] });
            setCurrentPage(page);
        } catch (error) {
            console.error('Erreur lors du chargement des annonces:', error);
            setError('Erreur lors du chargement des annonces');
            setAnnoncesData({ count: 0, next: null, previous: null, results: [] });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchAnnonces(1, searchTerm, activeTab);
    }, [activeTab]);

    // Gérer la recherche avec debounce
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            fetchAnnonces(1, searchTerm, activeTab);
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    // Filtrage local des annonces
    useEffect(() => {
        let filtered = annoncesData.results;

        // Filtrer par catégorie si une catégorie spécifique est sélectionnée
        if (categoryFilter !== 'toutes') {
            filtered = filtered.filter(annonce => {
                // Essayer de déduire la catégorie depuis le contenu ou les données d'immatriculation
                const content = annonce.contenu_complet || '';
                const isConstitution = content.includes('constitué') || content.includes('CONSTITUTION');
                const isModification = content.includes('MODIFICATION') || content.includes('modification');
                const isRadiation = content.includes('RADIATION') || content.includes('radiation');

                switch (categoryFilter.toLowerCase()) {
                    case 'immatriculation':
                        return isConstitution;
                    case 'modification':
                        return isModification;
                    case 'radiation':
                        return isRadiation;
                    default:
                        return true;
                }
            });
        }

        setFilteredAnnonces(filtered);
    }, [annoncesData.results, categoryFilter]);

    const handleTabChange = (tab) => {
        navigate(`/dashboard/annonces/${tab}`);
    };

    // Gérer la pagination
    const handlePageChange = (direction) => {
        if (direction === 'next' && annoncesData.next) {
            fetchAnnonces(currentPage + 1, searchTerm, activeTab);
        } else if (direction === 'prev' && annoncesData.previous) {
            fetchAnnonces(currentPage - 1, searchTerm, activeTab);
        }
    };

    // Aller à une page spécifique
    const goToPage = (pageNumber) => {
        if (pageNumber >= 1) {
            fetchAnnonces(pageNumber, searchTerm, activeTab);
        }
    };

    // Gérer l'affichage du détail d'une annonce
    const handleView = (annonce) => {
        setSelectedAnnonce(annonce);
        setShowViewModal(true);
    };

    // Gérer l'édition d'une annonce
    const handleEdit = (annonce) => {
        setSelectedAnnonce(annonce);
        setShowEditModal(true);
    };

    // Gérer la suppression d'une annonce
    const handleDelete = async (annonceId) => {
        if (window.confirm('Êtes-vous sûr de vouloir supprimer cette annonce ?')) {
            try {
                setLoading(true);
                await Api.delete(`formalite/annonces/${annonceId}`);
                await fetchAnnonces(currentPage, searchTerm, activeTab);
                alert('Annonce supprimée avec succès !');
            } catch (error) {
                console.error('Erreur lors de la suppression:', error);
                alert('Erreur lors de la suppression de l\'annonce');
            } finally {
                setLoading(false);
            }
        }
    };

    // Gérer les actions spéciales (publier, payer, valider, rejeter)
    const handleSpecialAction = async (action, annonceId) => {
        try {
            setLoading(true);
            let endpoint = '';
            let payload = {};

            switch (action) {
                case 'publier':
                    endpoint = `formalite/annonces/${annonceId}/publier`;
                    break;
                case 'payer':
                    endpoint = `formalite/annonces/${annonceId}/payer`;
                    break;
                case 'valider':
                    endpoint = `formalite/annonces/${annonceId}/valider`;
                    break;
                case 'rejeter':
                    endpoint = `formalite/annonces/${annonceId}/rejeter`;
                    break;
                default:
                    return;
            }

            await Api.post(endpoint, payload);
            await fetchAnnonces(currentPage, searchTerm, activeTab);
            alert(`Action "${action}" effectuée avec succès !`);
        } catch (error) {
            console.error(`Erreur lors de l'action ${action}:`, error);
            alert(`Erreur lors de l'action ${action}`);
        } finally {
            setLoading(false);
        }
    };

    // Gérer le succès des opérations de modal
    const handleModalSuccess = async (message) => {
        alert(message);
        await fetchAnnonces(currentPage, searchTerm, activeTab);
        setShowViewModal(false);
        setShowEditModal(false);
        setSelectedAnnonce(null);
    };

    // Gérer la fermeture des modals
    const handleCloseModals = () => {
        setShowViewModal(false);
        setShowEditModal(false);
        setSelectedAnnonce(null);
    };

    // Exporter les données
    const handleExport = async () => {
        try {
            const response = await Api.get(`${ANNONCES_ENDPOINT}/export`);
            // Traitement de l'export selon le format de réponse
            alert('Export réussi !');
        } catch (error) {
            console.error('Erreur lors de l\'export:', error);
            alert('Erreur lors de l\'export');
        }
    };

    // Imprimer la liste
    const handlePrint = () => {
        window.print();
    };

    const getStatutBadge = (statut) => {
        const classes = {
            'brouillon': 'statut-en-attente',
            'en-attente': 'statut-en-attente',
            'En attente': 'statut-en-attente',
            'validee': 'statut-validee',
            'validée': 'statut-validee',
            'Validée': 'statut-validee',
            'publiee': 'statut-publiee',
            'publiée': 'statut-publiee',
            'Publiée': 'statut-publiee',
            'rejetee': 'statut-rejetee',
            'rejetée': 'statut-rejetee',
            'Rejetée': 'statut-rejetee'
        };
        return classes[statut] || 'statut-en-attente';
    };

    const getPaiementBadge = (paiement) => {
        const classes = {
            'En attente': 'paiement-en-attente',
            'en-attente': 'paiement-en-attente',
            'Payé': 'paiement-paye',
            'paye': 'paiement-paye',
            'Remboursé': 'paiement-rembourse',
            'rembourse': 'paiement-rembourse'
        };
        return classes[paiement] || 'paiement-en-attente';
    };

    // Extraire le nom de société depuis le contenu ou les données structurées
    const extractSocieteNom = (annonce) => {
        if (annonce.nom_societe) return annonce.nom_societe;

        // Essayer d'extraire depuis le contenu_complet
        const content = annonce.contenu_complet || '';
        const matches = content.match(/dénommée\s*:\s*([^.\n]+)/i) ||
            content.match(/société\s*([^,\n]+)/i);

        return matches ? matches[1].trim() : 'N/A';
    };

    // Extraire la forme juridique
    const extractFormeJuridique = (annonce) => {
        if (annonce.forme_juridique) return annonce.forme_juridique;
        if (annonce.immatriculation_details?.forme_juridique_libelle) {
            return annonce.immatriculation_details.forme_juridique_libelle;
        }

        // Essayer d'extraire depuis le contenu
        const content = annonce.contenu_complet || '';
        const formes = ['SARL', 'SA', 'SAS', 'SASU', 'EURL', 'SCI', 'SNC', 'Société Civile Immobilière', 'Société à Responsabilité Limitée'];

        for (const forme of formes) {
            if (content.includes(forme)) {
                return forme;
            }
        }

        return 'N/A';
    };

    // Calculer les totaux pour chaque onglet
    const getTabCounts = () => {
        const results = annoncesData.results;
        return {
            'en-attente': results.filter(a => a.statut === 'brouillon').length,
            'validees': results.filter(a => ['validee', 'validée'].includes(a.statut)).length,
            'publiees': results.filter(a => ['publiee', 'publiée'].includes(a.statut)).length,
            'rejetees': results.filter(a => ['rejetee', 'rejetée'].includes(a.statut)).length,
            'toutes': annoncesData.count
        };
    };

    // Calculer le nombre total de pages
    const totalPages = Math.ceil(annoncesData.count / 20); // Supposons 20 éléments par page

    const tabCounts = getTabCounts();

    if (loading && filteredAnnonces.length === 0) {
        return (
            <div className="loading-container">
                <div className="loading-spinner"></div>
                <p>Chargement des annonces...</p>
            </div>
        );
    }

    return (
        <div className="annonces-container">
            {/* Header */}
            <div className="annonces-header">
                <div className="search-container">
                    <Search size={20} className="search-icon" />
                    <input
                        type="text"
                        placeholder="Rechercher une annonce..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <div className="header-actions">
                    {error && (
                        <div className="error-message">
                            {error}
                            <button onClick={() => fetchAnnonces(currentPage, searchTerm, activeTab)} className="retry-button">
                                Réessayer
                            </button>
                        </div>
                    )}

                    <button className="action-button" onClick={handlePrint} disabled={loading}>
                        <Printer size={16} />
                        Imprimer la liste
                    </button>
                    <button className="action-button" onClick={handleExport} disabled={loading}>
                        <Download size={16} />
                        Exporter
                    </button>

                    <div className="category-filter">
                        <span>Catégorie:</span>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="filter-select"
                        >
                            <option value="toutes">Toutes les catégories</option>
                            <option value="immatriculation">Immatriculation</option>
                            <option value="modification">Modification</option>
                            <option value="radiation">Radiation</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Onglets */}
            <div className="tabs-container">
                <button
                    className={`tab ${activeTab === 'en-attente' ? 'tab-active' : ''}`}
                    onClick={() => handleTabChange('en-attente')}
                >
                    En attente ({tabCounts['en-attente']})
                </button>
                <button
                    className={`tab ${activeTab === 'validees' ? 'tab-active' : ''}`}
                    onClick={() => handleTabChange('validees')}
                >
                    Validées ({tabCounts['validees']})
                </button>
                <button
                    className={`tab ${activeTab === 'publiees' ? 'tab-active' : ''}`}
                    onClick={() => handleTabChange('publiees')}
                >
                    Publiées ({tabCounts['publiees']})
                </button>
                <button
                    className={`tab ${activeTab === 'rejetees' ? 'tab-active' : ''}`}
                    onClick={() => handleTabChange('rejetees')}
                >
                    Rejetées ({tabCounts['rejetees']})
                </button>
                <button
                    className={`tab ${activeTab === 'toutes' ? 'tab-active' : ''}`}
                    onClick={() => handleTabChange('toutes')}
                >
                    Toutes ({tabCounts['toutes']})
                </button>
            </div>

            {/* Table */}
            <div className="table-container">
                <table className="annonces-table">
                    <thead>
                        <tr>
                            <th>Société</th>
                            <th>Forme juridique</th>
                            <th>Date de création</th>
                            <th>Date de parution</th>
                            <th>Référence</th>
                            <th>Catégorie</th>
                            <th>Statut</th>
                            <th>Paiement</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredAnnonces.length === 0 ? (
                            <tr>
                                <td colSpan="9" className="no-data">
                                    {searchTerm ? 'Aucune annonce trouvée' : 'Aucune annonce'}
                                </td>
                            </tr>
                        ) : (
                            filteredAnnonces.map((annonce) => (
                                <tr key={annonce.id}>
                                    <td className="company-name">
                                        {extractSocieteNom(annonce)}
                                    </td>
                                    <td>{extractFormeJuridique(annonce)}</td>
                                    <td>
                                        {annonce.created_at ?
                                            new Date(annonce.created_at).toLocaleDateString('fr-FR') :
                                            'N/A'
                                        }
                                    </td>
                                    <td>
                                        {annonce.date_publication ?
                                            new Date(annonce.date_publication).toLocaleDateString('fr-FR') :
                                            'N/A'
                                        }
                                    </td>
                                    <td className="reference">
                                        {annonce.immatriculation_details?.reference || 'N/A'}
                                    </td>
                                    <td>
                                        {annonce.contenu_complet?.includes('MODIFICATION') ? 'Modification' :
                                            annonce.contenu_complet?.includes('RADIATION') ? 'Radiation' :
                                                annonce.contenu_complet?.includes('constitué') ? 'Immatriculation' : 'N/A'}
                                    </td>
                                    <td>
                                        <span className={`statut-badge ${getStatutBadge(annonce.statut)}`}>
                                            {annonce.statut === 'brouillon' ? 'En attente' : annonce.statut || 'N/A'}
                                        </span>
                                    </td>
                                    <td>
                                        <span className={`paiement-badge ${getPaiementBadge('En attente')}`}>
                                            En attente
                                        </span>
                                    </td>
                                    <td>
                                        <div className="actions-menu">
                                            <button
                                                className="action-icon"
                                                onClick={() => handleView(annonce)}
                                                title="Voir"
                                                disabled={loading}
                                            >
                                                <Eye size={16} />
                                            </button>
                                            <button
                                                className="action-icon"
                                                onClick={() => handleEdit(annonce)}
                                                title="Éditer"
                                                disabled={loading}
                                            >
                                                <Edit size={16} />
                                            </button>
                                            <button
                                                className="action-icon delete"
                                                onClick={() => handleDelete(annonce.id)}
                                                title="Supprimer"
                                                disabled={loading}
                                            >
                                                <Trash2 size={16} />
                                            </button>
                                            {annonce.statut === 'brouillon' && (
                                                <>
                                                    <button
                                                        className="action-button publier"
                                                        onClick={() => handleSpecialAction('publier', annonce.id)}
                                                        disabled={loading}
                                                    >
                                                        Publier
                                                    </button>
                                                    <button
                                                        className="action-button payer"
                                                        onClick={() => handleSpecialAction('payer', annonce.id)}
                                                        disabled={loading}
                                                    >
                                                        Payé
                                                    </button>
                                                </>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Pagination améliorée */}
            {annoncesData.count > 0 && (
                <div className="pagination-container">
                    <div className="pagination-info">
                        <span>
                            Affichage de {(currentPage - 1) * 20 + 1} à {Math.min(currentPage * 20, annoncesData.count)}
                            sur {annoncesData.count} annonce(s)
                        </span>
                    </div>

                    <div className="pagination-controls">
                        <button
                            className="pagination-button"
                            onClick={() => goToPage(1)}
                            disabled={currentPage === 1 || loading}
                        >
                            «
                        </button>

                        <button
                            className="pagination-button"
                            onClick={() => handlePageChange('prev')}
                            disabled={!annoncesData.previous || loading}
                        >
                            ‹ Précédent
                        </button>

                        {/* Pages numbers */}
                        <div className="page-numbers">
                            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                let pageNum;
                                if (totalPages <= 5) {
                                    pageNum = i + 1;
                                } else if (currentPage <= 3) {
                                    pageNum = i + 1;
                                } else if (currentPage >= totalPages - 2) {
                                    pageNum = totalPages - 4 + i;
                                } else {
                                    pageNum = currentPage - 2 + i;
                                }

                                return (
                                    <button
                                        key={pageNum}
                                        className={`page-number ${currentPage === pageNum ? 'active' : ''}`}
                                        onClick={() => goToPage(pageNum)}
                                        disabled={loading}
                                    >
                                        {pageNum}
                                    </button>
                                );
                            })}
                        </div>

                        <button
                            className="pagination-button"
                            onClick={() => handlePageChange('next')}
                            disabled={!annoncesData.next || loading}
                        >
                            Suivant ›
                        </button>

                        <button
                            className="pagination-button"
                            onClick={() => goToPage(totalPages)}
                            disabled={currentPage === totalPages || loading}
                        >
                            »
                        </button>
                    </div>
                </div>
            )}

            {/* Modals */}
            <ViewAnnonceModal
                isOpen={showViewModal}
                onClose={handleCloseModals}
                annonce={selectedAnnonce}
                onAction={handleSpecialAction}
            />

            <EditAnnonceModal
                isOpen={showEditModal}
                onClose={handleCloseModals}
                onSuccess={handleModalSuccess}
                annonce={selectedAnnonce}
            />

            {/* Routes pour les sous-pages */}
            <Routes>
                <Route path="/" element={<Navigate to="en-attente" replace />} />
                <Route path="/en-attente" element={<div />} />
                <Route path="/validees" element={<div />} />
                <Route path="/publiees" element={<div />} />
                <Route path="/rejetees" element={<div />} />
                <Route path="/toutes" element={<div />} />
            </Routes>
        </div>
    );
};

export default Annonces;